import type React from "react";
import { Box, Link, Typography } from "@mui/material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import DetailSectionCard from "../common/DetailSectionCard";
import UserAvatar from "../common/UserAvatar";
import type { StandbyOrder } from "../../types/standbyOrder";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

interface StandbyOrderContextSummaryCardsProps {
  order: StandbyOrder;
}

const FieldBlock: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }} component="div">
      {value}
    </Typography>
  </Box>
);

const PersonValue: React.FC<{
  user: { _id: string; name?: string; profilePictureUrl?: string | null };
}> = ({ user }) => {
  const name = user.name || user._id;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <UserAvatar user={user} size={28} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {name}
      </Typography>
    </Box>
  );
};

const formatCreationTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const getCreationInfo = (
  order: StandbyOrder,
): {
  timestamp: number;
  userId: string;
  userName?: string;
  profilePictureUrl?: string | null;
} | null => {
  const histories = order.histories ?? [];
  if (histories.length === 0) {
    return null;
  }

  const oldest = [...histories].sort((a, b) => a.timestamp - b.timestamp)[0];
  return {
    timestamp: oldest.timestamp,
    userId: oldest.userId,
    userName: oldest.userName,
    profilePictureUrl: oldest.profilePictureUrl,
  };
};

const StandbyOrderContextSummaryCards: React.FC<StandbyOrderContextSummaryCardsProps> = ({
  order,
}) => {
  const { t } = useTranslation();
  const creation = getCreationInfo(order);
  const createdByUser = creation
    ? {
        _id: creation.userId,
        name: creation.userName || order.creator?.name || creation.userId,
        profilePictureUrl: creation.profilePictureUrl,
      }
    : order.creator
      ? { _id: order.creator._id, name: order.creator.name }
      : null;
  const reportUrl = order.uangJalanReportReadableId
    ? `/uang-jalan-reports/${order.uangJalanReportReadableId}`
    : null;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        mb: 2,
      }}
    >
      <DetailSectionCard
        title={t("orders.generalSection" as TranslationKeys)}
        icon={<AssignmentIndOutlinedIcon fontSize="small" />}
        iconColor="#7C3AED"
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <FieldBlock
            label={t("Team" as TranslationKeys)}
            value={order.teamOperations?.name || "—"}
          />
          {order.note ? (
            <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.STANDBY.NOTE}>
              <FieldBlock
                label={t("orders.note" as TranslationKeys)}
                value={
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>
                    {order.note}
                  </Typography>
                }
              />
            </Box>
          ) : null}
          <FieldBlock
            label={t("orders.createdAt" as TranslationKeys)}
            value={creation ? formatCreationTimestamp(creation.timestamp) : "—"}
          />
          <FieldBlock
            label={t("orders.createdBy" as TranslationKeys)}
            value={createdByUser ? <PersonValue user={createdByUser} /> : "—"}
          />
          <FieldBlock
            label={t("orders.report" as TranslationKeys)}
            value={
              reportUrl && order.uangJalanReportReadableId ? (
                <Link
                  component={RouterLink}
                  to={reportUrl}
                  underline="hover"
                  data-testid={TestId.STANDBY.VIEW_REPORT}
                  sx={{ fontWeight: 600 }}
                >
                  {order.uangJalanReportReadableId}
                </Link>
              ) : (
                "—"
              )
            }
          />
        </Box>
      </DetailSectionCard>

      <DetailSectionCard
        title={t("orders.vehicleAndRouteSection" as TranslationKeys)}
        icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
        iconColor="#16A34A"
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <FieldBlock
            label={t("License Plate" as TranslationKeys)}
            value={
              order.truck?.licensePlate ? formatLicensePlateInput(order.truck.licensePlate) : "—"
            }
          />
          <FieldBlock
            label={t("DRIVER" as TranslationKeys)}
            value={
              order.driver ? (
                <PersonValue
                  user={{
                    _id: order.driver._id,
                    name: order.driver.name,
                    profilePictureUrl: order.driver.profilePictureUrl,
                  }}
                />
              ) : (
                "—"
              )
            }
          />
          <FieldBlock
            label={t("Location" as TranslationKeys)}
            value={order.location?.name || "—"}
          />
          <FieldBlock label={t("standby.date" as TranslationKeys)} value={order.date || "—"} />
        </Box>
      </DetailSectionCard>
    </Box>
  );
};

export default StandbyOrderContextSummaryCards;
